import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useToast } from "../context/ToastContext";

export default function SupportUs() {
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  

  const donationOptions = [
    { amount: 49, label: "☕ Small Support" },
    { amount: 99, label: "📚 Learning Support" },
    { amount: 199, label: "🚀 Growth Support" },
    { amount: 499, label: "💡 Feature Support" },
    { amount: 999, label: "❤️ Community Support" },
  ];
  


  const handlePayment = async () => {
    if (!selectedAmount || selectedAmount <= 0) {
      showToast("❌ Please enter a valid amount.");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/payment/create-order`,
        { amount: selectedAmount * 100 }
      );

      const options = {
        key: data.key,
        amount: data.amount,
        currency: "INR",
        name: "BharatPariksha",
        description: "Support learning & open testing in India",
        order_id: data.orderId,

        handler: async function (response) {
          const verify = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`,
            response
          );

          if (verify.data.success) {
            showToast(`🙏 Thank you for supporting BharatPariksha with ₹${selectedAmount}!`);
          } else {
            showToast("❌ Payment verification failed.");
          }
        },

        theme: { color: "#7A4A28" },
        method: { upi: true, card: true, netbanking: true },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error(err);
      showToast("❌ Failed to start payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EF] px-6 py-20 text-[#3A2A1A] flex flex-col items-center">

      {/* HERO */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl text-center mb-16">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Support BharatPariksha 💛</h1>
        <p className="text-[#5C4632] text-lg">
          BharatPariksha is built to stay free. Your support helps us keep learning accessible.
        </p>
      </motion.div>

      {/* CARD */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#F4EBDD] border border-[#E6D8C3] rounded-2xl shadow-xl px-10 py-12 max-w-md w-full text-center"
      >
        <h2 className="text-2xl font-bold mb-4">
          Support <span className="text-[#7A4A28]">Our Mission</span>
        </h2>

        <div className="grid grid-cols-2 gap-3 text-sm text-[#5C4632] mb-8">
          <p>🖥️ Servers</p>
          <p>🚀 Features</p>
          <p>🧠 Learning</p>
          <p>🛡️ Security</p>
        </div>

        {/* Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {donationOptions.map(({ amount, label }) => (
            <motion.button
              key={amount}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedAmount(amount)}
              className={`px-5 py-3 rounded-xl border text-sm font-medium transition
              ${selectedAmount === amount
                  ? "bg-[#7A4A28] text-white border-[#7A4A28]"
                  : "bg-[#FAF6EF] text-[#7A4A28] border-[#E6D8C3]"}`}
            >
              <div className="font-semibold">₹{amount}</div>
              <div className="text-xs">{label}</div>
            </motion.button>
          ))}
        </div>

        {/* Custom Amount */}
        <input
          type="number"
          min="1"
          value={selectedAmount}
          onChange={(e) => setSelectedAmount(Number(e.target.value))}
          placeholder="Enter custom amount ₹"
          className="w-full px-4 py-2 border border-[#E6D8C3] rounded-lg text-center mb-6"
        />

        {/* Donate */}
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={handlePayment}
          disabled={loading}
          className="bg-[#7A4A28] text-white px-10 py-3 rounded-xl font-medium shadow-md hover:bg-[#5C4632]"
        >
          {loading ? "Processing..." : `Donate ₹${selectedAmount}`}
        </motion.button>

        <p className="text-xs text-[#5C4632] mt-6 italic">
          BharatPariksha will always remain usable without payment.
        </p>
      </motion.div>

      <footer className="mt-24 text-sm text-[#5C4632] text-center">
        © {new Date().getFullYear()} BharatPariksha
      </footer>
    </div>
  );
}
